import { useCallback, useMemo, useState } from "react";
import {
  Button,
  Flex,
  Form,
  Heading,
  Icon,
  Icons,
  Modal,
  Paragraph,
  Span,
  useToast,
} from "@ir/ui";
import {
  CreateMasterDataDeductionCategoryDTO,
  DeleteMasterDataDeductionCategoriesDTO,
  UpdateMasterDataDeductionCategoryDTO,
} from "@src/domain/dto/masterData";
import type { MasterDataDeductionCategory } from "@src/domain/entities/masterData";
import { Permissions } from "@src/domain/permissions";
import {
  CreateMasterDataDeductionCategoryUseCase,
  DeleteMasterDataDeductionCategoriesUseCase,
  UpdateMasterDataDeductionCategoryUseCase,
} from "@src/application/useCases/masterData";
import { MasterDataDeductionCategoryRepository } from "@src/infrastructure/repositories/masterData";
import HasAnyPermission from "@src/presentation/components/permissions/HasAnyPermission";
import { useUserPermissions } from "@src/presentation/hooks/permissions";
import { useMasterDataDeductionCategories } from "@src/presentation/providers/MasterDataDeductionCategoriesDataProvider";
import { MasterDataAddEditModal } from "../utils/MasterDataAddEditModal";
import {
  MasterDataDeductionCategoryAddEditForm,
  type DeductionCategoryAddEditFormValues,
} from "../utils/MasterDataDeductionCategoryAddEditForm";
import { MasterDataDeductionCategoriesTable } from "../utils/MasterDataDeductionCategoriesTable";
import { MasterDataWritableListLayout } from "../utils/MasterDataWritableListLayout";
import { getMasterDataSaveErrorMessage } from "../utils/masterDataSaveErrors";
import {
  DEDUCTION_CATEGORY_KEY_ALREADY_EXISTS_MESSAGE,
  isDeductionCategoryKeyDuplicateError,
} from "../utils/deductionCategoryKeyValidation";
import {
  formatMasterDataRecordCount,
  useMasterDataAddEditModal,
} from "../utils/useMasterDataAddEditModal";

const EMPTY_FORM_VALUES: DeductionCategoryAddEditFormValues = {
  categoryName: "",
  categoryKey: "",
  isActive: false,
};

const LINKED_DELETE_WARNING =
  "Cannot delete category assigned to active deduction types.";

export const MasterDataDeductionCategoriesPage = () => {
  const { deductionCategories, loading, search, setSearch, refetch } =
    useMasterDataDeductionCategories();
  const { success: successToast, error: errorToast } = useToast();
  const { hasAnyPermission } = useUserPermissions();
  const [form] = Form.useForm<DeductionCategoryAddEditFormValues>();
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const canDelete = hasAnyPermission([Permissions.MASTER_DATA_DELETE]);

  const {
    modalMode,
    editingEntity: editingCategory,
    isSaving,
    setIsSaving,
    isFormSubmittable,
    setIsFormSubmittable,
    closeModal: closeModalState,
    openCreateModal,
    openEditModal,
  } = useMasterDataAddEditModal<MasterDataDeductionCategory>();

  const repository = useMemo(
    () => new MasterDataDeductionCategoryRepository(),
    [],
  );
  const createUseCase = useMemo(
    () => new CreateMasterDataDeductionCategoryUseCase(repository),
    [repository],
  );
  const updateUseCase = useMemo(
    () => new UpdateMasterDataDeductionCategoryUseCase(repository),
    [repository],
  );
  const deleteUseCase = useMemo(
    () => new DeleteMasterDataDeductionCategoriesUseCase(repository),
    [repository],
  );

  const initialValues = useMemo((): DeductionCategoryAddEditFormValues => {
    if (modalMode === "edit" && editingCategory) {
      return {
        categoryName: editingCategory.categoryName,
        categoryKey: editingCategory.categoryKey,
        isActive: editingCategory.isActive,
      };
    }
    return EMPTY_FORM_VALUES;
  }, [editingCategory, modalMode]);

  const closeModal = useCallback(() => {
    closeModalState(form);
  }, [closeModalState, form]);

  const handleSubmit = useCallback(
    async (values: DeductionCategoryAddEditFormValues) => {
      setIsSaving(true);
      try {
        const categoryName = values.categoryName.trim();
        const categoryKey = values.categoryKey.trim();
        const isActive = Boolean(values.isActive);

        if (modalMode === "edit" && editingCategory) {
          await updateUseCase.execute(
            new UpdateMasterDataDeductionCategoryDTO({
              categoryId: editingCategory.categoryId,
              categoryName,
              categoryKey: editingCategory.categoryKey,
              isActive,
            }),
          );
        } else {
          await createUseCase.execute(
            new CreateMasterDataDeductionCategoryDTO({
              categoryName,
              categoryKey,
              isActive,
            }),
          );
        }

        successToast({
          content: "Deduction category saved successfully",
        });
        closeModal();
        refetch();
      } catch (err) {
        if (isDeductionCategoryKeyDuplicateError(err)) {
          form.setFields([
            {
              name: "categoryKey",
              errors: [DEDUCTION_CATEGORY_KEY_ALREADY_EXISTS_MESSAGE],
            },
          ]);
          setIsFormSubmittable(false);
        } else {
          errorToast({
            content: getMasterDataSaveErrorMessage(
              err,
              "Failed to save deduction category. Please try again.",
              "Failed to save deduction category. Please try again later.",
            ),
          });
        }
      } finally {
        setIsSaving(false);
      }
    },
    [
      closeModal,
      createUseCase,
      editingCategory,
      errorToast,
      form,
      modalMode,
      refetch,
      setIsFormSubmittable,
      setIsSaving,
      successToast,
      updateUseCase,
    ],
  );

  const handleConfirmDelete = useCallback(async () => {
    const categoryIds = Array.from(selectedIds);
    if (categoryIds.length === 0) {
      setIsDeleteConfirmOpen(false);
      return;
    }

    setIsDeleting(true);
    try {
      const result = await deleteUseCase.execute(
        new DeleteMasterDataDeductionCategoriesDTO({ categoryIds }),
      );

      if (result.deletedCount > 0) {
        successToast({
          content: `Deleted ${result.deletedCount} ${
            result.deletedCount === 1 ? "category" : "categories"
          }.`,
        });
      }
      if (result.blockedCount > 0) {
        errorToast({ content: LINKED_DELETE_WARNING });
      }

      setSelectedIds(new Set());
      setIsDeleteConfirmOpen(false);
      refetch();
    } catch (err) {
      const message =
        err instanceof Error && err.message.trim()
          ? err.message
          : "Failed to delete deduction categories. Please try again.";
      errorToast({ content: message });
    } finally {
      setIsDeleting(false);
    }
  }, [deleteUseCase, errorToast, refetch, selectedIds, successToast]);

  const selectedCount = selectedIds.size;

  return (
    <>
      <MasterDataWritableListLayout
        title="Deduction Categories"
        subtitle={formatMasterDataRecordCount(
          loading,
          deductionCategories.length,
        )}
        searchPlaceholder="Search deduction categories"
        search={search}
        setSearch={setSearch}
        searchSection="deduction-categories"
        addButtonLabel="Add Deduction Category"
        onAdd={openCreateModal}
        headerActions={
          <HasAnyPermission
            requiredPermissions={[Permissions.MASTER_DATA_DELETE]}
          >
            {selectedCount > 0 ? (
              <Button
                size="small"
                color="error"
                variant="outline"
                onPress={() => setIsDeleteConfirmOpen(true)}
                className="ms-2"
              >
                <Icon slot="start" name={Icons.Trash} />
                Delete Selected
              </Button>
            ) : null}
          </HasAnyPermission>
        }
        hideSearch={selectedCount > 0}
        loading={loading}
      >
        <MasterDataDeductionCategoriesTable
          deductionCategories={deductionCategories}
          search={search}
          setSearch={setSearch}
          onEdit={openEditModal}
          showSelection={canDelete}
          selectedIds={selectedIds}
          onSelectionChange={setSelectedIds}
        />
      </MasterDataWritableListLayout>

      <MasterDataAddEditModal
        mode={modalMode}
        createTitle="Add Deduction Category"
        editTitle="Edit Deduction Category"
        isSaving={isSaving}
        isFormSubmittable={isFormSubmittable}
        form={form}
        onClose={closeModal}
      >
        <MasterDataDeductionCategoryAddEditForm
          form={form}
          mode={modalMode ?? "create"}
          initialValues={initialValues}
          isLinked={
            modalMode === "edit" ? Boolean(editingCategory?.isLinked) : false
          }
          onSubmit={handleSubmit}
          onFormValidityChange={setIsFormSubmittable}
        />
      </MasterDataAddEditModal>

      <Modal
        isOpen={isDeleteConfirmOpen}
        onClose={() => {
          if (!isDeleting) setIsDeleteConfirmOpen(false);
        }}
        size="small"
        closable={!isDeleting}
        headerContent={
          <Heading level={6} className="text-grey-700 font-bold m-0">
            Delete selected categories
          </Heading>
        }
        footerContent={
          <Flex gap={0.5} className="flex-1" justifyContent="space-between">
            <Button
              size="small"
              color="grey"
              isDisabled={isDeleting}
              onPress={() => setIsDeleteConfirmOpen(false)}
            >
              Cancel
            </Button>
            <Button
              size="small"
              color="error"
              isLoading={isDeleting}
              isDisabled={isDeleting}
              onPress={() => {
                void handleConfirmDelete();
              }}
            >
              Delete Selected
            </Button>
          </Flex>
        }
        classNames={{
          body: "p-0",
        }}
      >
        <Paragraph className="m-0 py-4 px-3">
          Are you sure you want to delete{" "}
          <Span className="font-bold text-primary-500">{selectedCount}</Span>
          <Span className="ps-1">
            {selectedCount === 1 ? "category" : "categories"}
          </Span>
          ?
        </Paragraph>
      </Modal>
    </>
  );
};
